import { useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'
import { useLocation } from '../../contexts/LocationContext'

// Institution data processing utilities
const EDUCATION_LEVELS = ['Primary', 'Secondary', 'Tertiary']
const OWNERSHIP_TYPES = ['Government', 'Private']

// Calculate institution metrics from raw data
function calculateInstitutionMetrics(data) {
  const aggregatedData = {}

  // Group by year, district, level, ownership
  data.forEach(record => {
    const year = parseInt(record.year)
    const level = record.level
    const ownership = record.ownership
    const value = parseInt(record.value) || 0

    const key = `${year}-${record.district}-${record.subcounty || 'Unknown'}`

    if (!aggregatedData[key]) {
      aggregatedData[key] = {
        year,
        district: record.district,
        subcounty: record.subcounty,
        county: record.county,
        location_code: record.location_code,
        levels: {},
        ownership: {},
        total: 0,
      }

      // Initialize all combinations
      EDUCATION_LEVELS.forEach(lvl => {
        aggregatedData[key].levels[lvl] = {
          Government: 0,
          Private: 0,
          total: 0,
        }
      })
      OWNERSHIP_TYPES.forEach(own => {
        aggregatedData[key].ownership[own] = 0
      })
    }

    // Add to totals
    aggregatedData[key].levels[level][ownership] += value
    aggregatedData[key].levels[level].total += value
    aggregatedData[key].ownership[ownership] += value
    aggregatedData[key].total += value
  })

  return Object.values(aggregatedData)
}

// Aggregate data by year for trend analysis
function aggregateByYear(processedData) {
  const yearData = {}

  processedData.forEach(record => {
    const year = record.year

    if (!yearData[year]) {
      yearData[year] = {
        year,
        total: 0,
        levels: {},
        ownership: { Government: 0, Private: 0 },
        privatizationRates: {},
      }

      EDUCATION_LEVELS.forEach(level => {
        yearData[year].levels[level] = { Government: 0, Private: 0, total: 0 }
        yearData[year].privatizationRates[level] = 0
      })
    }

    const yearRecord = yearData[year]

    // Aggregate totals
    yearRecord.total += record.total

    // Aggregate by levels and ownership
    EDUCATION_LEVELS.forEach(level => {
      yearRecord.levels[level].Government += record.levels[level].Government
      yearRecord.levels[level].Private += record.levels[level].Private
      yearRecord.levels[level].total += record.levels[level].total
    })

    yearRecord.ownership.Government += record.ownership.Government
    yearRecord.ownership.Private += record.ownership.Private
  })

  // Calculate privatization rates
  Object.values(yearData).forEach(yearRecord => {
    EDUCATION_LEVELS.forEach(level => {
      const total = yearRecord.levels[level].total
      yearRecord.privatizationRates[level] =
        total > 0 ? (yearRecord.levels[level].Private / total) * 100 : 0
    })
  })

  return Object.values(yearData).sort((a, b) => a.year - b.year)
}

// Process data for Geographic Education Equity Heatmap
function processGeographicEquityHeatmap(processedData, locationLevel) {
  if (!locationLevel) return null

  const heatmapData = {}

  processedData.forEach(record => {
    if (!record[locationLevel]) return

    const location = record[locationLevel]
    const year = record.year
    const key = `${location}-${year}`

    if (!heatmapData[key]) {
      heatmapData[key] = {
        location,
        year,
        total: 0,
      }
    }

    heatmapData[key].total += record.total || 0
  })

  // Calculate institution density and organize data
  const locations = new Set()
  const years = new Set()
  const data = []

  Object.values(heatmapData).forEach(record => {
    locations.add(record.location)
    years.add(record.year)

    // Color coding based on total institutions (adjust thresholds as needed)
    let color = '#ef4444' // Red for low density
    if (record.total >= 30)
      color = '#10b981' // Green for high density
    else if (record.total >= 15) color = '#f59e0b' // Yellow for moderate density

    data.push({
      location: record.location,
      year: record.year,
      total: record.total,
      color,
    })
  })

  return {
    locations: Array.from(locations).sort(),
    years: Array.from(years).sort((a, b) => a - b),
    data,
  }
}

function InstitutionCountTrends({ data }) {
  const {
    selectedDistrict,
    selectedSubcounty,
    selectedParish,
    selectedVillage,
  } = useLocation()

  // Process data for visualizations
  const processedData = useMemo(() => {
    if (!data || !data.length) return []
    return calculateInstitutionMetrics(data)
  }, [data])

  const yearlyData = useMemo(() => {
    return aggregateByYear(processedData)
  }, [processedData])

  // Determine location level for heatmap (show NEXT level)
  const locationLevel = useMemo(() => {
    if (selectedVillage) return null // No sub-levels below village
    if (selectedParish) return 'village' // Show villages within parish
    if (selectedSubcounty) return 'parish' // Show parishes within subcounty
    if (selectedDistrict) return 'subcounty' // Show subcounties within district
    return 'district' // Show districts when viewing all
  }, [selectedDistrict, selectedSubcounty, selectedParish, selectedVillage])

  // Process data for Geographic Education Equity Heatmap
  const geographicEquityHeatmapData = useMemo(() => {
    if (!processedData.length || !locationLevel) return null
    return processGeographicEquityHeatmap(processedData, locationLevel)
  }, [processedData, locationLevel])

  // 1. Educational Infrastructure Growth Trends
  const infrastructureGrowthOption = useMemo(() => {
    if (!yearlyData.length) return {}

    const years = yearlyData.map(d => d.year)
    const colors = {
      Primary: '#10b981', // Green
      Secondary: '#3b82f6', // Blue
      Tertiary: '#8b5cf6', // Purple
    }

    return {
      title: {
        show: false,
        text: 'Educational Infrastructure Growth Trends',
        left: 'center',
        padding: [0, 50],
        textStyle: { fontSize: 16, fontWeight: 'bold' },
      },
      tooltip: {
        trigger: 'axis',
        formatter: function (params) {
          let result = `<strong>Year ${params[0].axisValue}</strong><br/>`
          let total = 0
          params.forEach(param => {
            const value = parseInt(param.value) || 0
            total += value
            result += `${param.marker} ${param.seriesName}: ${value} institutions<br/>`
          })
          result += `<strong>Total: ${total} institutions</strong>`
          return result
        },
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      legend: {
        data: EDUCATION_LEVELS,
        top: 40,
        textStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: years,
        name: 'Year',
        nameLocation: 'middle',
        nameGap: 25,
        splitLine: {
          show: false,
        },
        nameTextStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      yAxis: {
        type: 'value',
        name: 'Number of Institutions',
        nameLocation: 'middle',
        nameGap: 40,
        splitLine: {
          show: false,
        },
        nameTextStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      series: EDUCATION_LEVELS.map(level => ({
        name: level,
        type: 'line',
        data: yearlyData.map(d => d.levels[level].total),
        lineStyle: { color: colors[level], width: 3 },
        itemStyle: { color: colors[level] },
        symbol: 'circle',
        symbolSize: 8,
        smooth: true,
      })),
    }
  }, [yearlyData])

  // 2. Public vs Private Sector Analysis
  const publicPrivateAnalysisOption = useMemo(() => {
    if (!yearlyData.length) return {}

    const years = yearlyData.map(d => d.year)

    return {
      title: {
        show: false,
        text: 'Public vs Private Sector Analysis',
        left: 'center',
        textStyle: { fontSize: 16, fontWeight: 'bold' },
      },
      tooltip: {
        trigger: 'axis',
        formatter: function (params) {
          let result = `<strong>Year ${params[0].axisValue}</strong><br/>`
          const govParam = params.find(p => p.seriesName === 'Government')
          const privParam = params.find(p => p.seriesName === 'Private')

          if (govParam && privParam) {
            const govValue = parseInt(govParam.value) || 0
            const privValue = parseInt(privParam.value) || 0
            const total = govValue + privValue

            result += `${govParam.marker} Government: ${govValue} (${total > 0 ? ((govValue / total) * 100).toFixed(1) : 0}%)<br/>`
            result += `${privParam.marker} Private: ${privValue} (${total > 0 ? ((privValue / total) * 100).toFixed(1) : 0}%)<br/>`
            result += `<strong>Total: ${total} institutions</strong>`
          }

          return result
        },
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      legend: {
        data: ['Government', 'Private'],
        top: 40,
        textStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: years,
        name: 'Year',
        splitLine: {
          show: false,
        },
        nameTextStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      yAxis: {
        type: 'value',
        name: 'Number of Institutions',
        nameLocation: 'middle',
        nameGap: 40,
        nameTextStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
        splitLine: {
          show: false,
        },
      },
      series: [
        {
          name: 'Government',
          type: 'bar',
          barGap: 0,
          data: yearlyData.map(d => d.ownership.Government),
          itemStyle: { color: '#10b981' },
        },
        {
          name: 'Private',
          type: 'bar',
          barGap: 0,
          data: yearlyData.map(d => d.ownership.Private),
          itemStyle: { color: '#f59e0b' },
        },
      ],
    }
  }, [yearlyData])

  // 3. Educational Access Pyramid
  const pyramidOption = useMemo(() => {
    if (!yearlyData.length) return {}

    const latestYear = yearlyData[yearlyData.length - 1]

    const pyramidData = [
      {
        name: 'Primary',
        value: latestYear.levels.Primary.total,
        itemStyle: { color: '#10b981' },
      },
      {
        name: 'Secondary',
        value: latestYear.levels.Secondary.total,
        itemStyle: { color: '#3b82f6' },
      },
      {
        name: 'Tertiary',
        value: latestYear.levels.Tertiary.total,
        itemStyle: { color: '#8b5cf6' },
      },
    ]

    return {
      title: {
        show: false,
        text: `Educational Access Pyramid (${latestYear.year})`,
        left: 'center',
        textStyle: { fontSize: 16, fontWeight: 'bold' },
      },
      tooltip: {
        formatter: function (params) {
          const total = pyramidData.reduce((sum, item) => sum + item.value, 0)
          const percentage =
            total > 0 ? ((params.value / total) * 100).toFixed(1) : 0
          return `${params.name}: ${params.value} institutions (${percentage}%)`
        },
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      series: [
        {
          name: 'Education Pyramid',
          type: 'funnel',
          left: '10%',
          top: 60,
          bottom: 60,
          width: '80%',
          min: 0,
          max: Math.max(...pyramidData.map(d => d.value)),
          minSize: '0%',
          maxSize: '100%',
          sort: 'descending',
          gap: 2,
          label: {
            show: true,
            position: 'inside',
            formatter: '{b}: {c}',
            fontSize: 14,
            fontWeight: 'bold',
            fontFamily:
              'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
          },
          labelLine: {
            length: 10,
            lineStyle: {
              width: 1,
              type: 'solid',
            },
          },
          itemStyle: {
            borderColor: '#fff',
            borderWidth: 1,
          },
          emphasis: {
            label: {
              fontSize: 16,
            },
          },
          data: pyramidData,
        },
      ],
    }
  }, [yearlyData])

  // 4. Private Sector Penetration by Level
  const privatePenetrationOption = useMemo(() => {
    if (!yearlyData.length) return {}

    const years = yearlyData.map(d => d.year)

    return {
      title: {
        show: false,
        text: 'Private Sector Penetration by Education Level',
        left: 'center',
        textStyle: { fontSize: 16, fontWeight: 'bold' },
      },
      tooltip: {
        trigger: 'axis',
        formatter: function (params) {
          let result = `<strong>Year ${params[0].axisValue}</strong><br/>`
          params.forEach(param => {
            const value = parseFloat(param.value) || 0
            result += `${param.marker} ${param.seriesName}: ${value.toFixed(1)}%<br/>`
          })
          return result
        },
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      legend: {
        data: EDUCATION_LEVELS,
        top: 40,
        textStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: years,
        name: 'Year',
        splitLine: {
          show: false,
        },
        nameTextStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      yAxis: {
        type: 'value',
        name: 'Private Sector Share (%)',
        nameLocation: 'middle',
        nameGap: 40,
        nameTextStyle: {
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
        max: 100,
        splitLine: {
          show: false,
        },
      },
      series: EDUCATION_LEVELS.map((level, index) => ({
        name: level,
        type: 'line',
        data: yearlyData.map(d => d.privatizationRates[level].toFixed(1)),
        lineStyle: {
          color: ['#10b981', '#3b82f6', '#8b5cf6'][index],
          width: 3,
        },
        itemStyle: { color: ['#10b981', '#3b82f6', '#8b5cf6'][index] },
        symbol: 'circle',
        symbolSize: 8,
        smooth: true,
      })),
    }
  }, [yearlyData])

  if (!data || !data.length) {
    return (
      <div className="flex items-center justify-center h-96 text-gray-500">
        <div className="text-center">
          <div className="text-4xl mb-2">🏫</div>
          <p>No institution count data available</p>
          <p className="text-sm mt-2">
            Please ensure institution count data is available for the selected
            location
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Summary Statistics */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          🏛️ Educational Infrastructure Summary
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {yearlyData.length > 0 &&
            (() => {
              const latest = yearlyData[yearlyData.length - 1]
              const govPercentage =
                latest.total > 0
                  ? (latest.ownership.Government / latest.total) * 100
                  : 0
              const privPercentage =
                latest.total > 0
                  ? (latest.ownership.Private / latest.total) * 100
                  : 0

              return (
                <>
                  <div className="bg-white p-4 rounded shadow-sm">
                    <div className="text-2xl font-bold text-blue-600">
                      {latest.total.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">
                      Total Institutions ({latest.year})
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded shadow-sm">
                    <div className="text-2xl font-bold text-green-600">
                      {latest.levels.Primary.total.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">Primary Schools</div>
                  </div>
                  <div className="bg-white p-4 rounded shadow-sm">
                    <div className="text-2xl font-bold text-purple-600">
                      {govPercentage.toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-600">
                      Government Institutions
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded shadow-sm">
                    <div className="text-2xl font-bold text-orange-600">
                      {privPercentage.toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-600">
                      Private Institutions
                    </div>
                  </div>
                </>
              )
            })()}
        </div>
      </div>

      {/* Educational Infrastructure Growth Trends */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Educational Infrastructure Growth Trends
          </h3>
          <p className="text-sm text-gray-600">
            Track how educational infrastructure is expanding across Primary,
            Secondary, and Tertiary levels over time
          </p>
        </div>
        <ReactECharts
          option={infrastructureGrowthOption}
          style={{ height: '500px' }}
          theme="ug-dashboard"
        />
      </div>

      {/* Public vs Private Sector Analysis */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Public vs Private Sector Analysis
          </h3>
          <p className="text-sm text-gray-600">
            Compare government versus private sector dominance in educational
            infrastructure provision
          </p>
        </div>
        <ReactECharts
          option={publicPrivateAnalysisOption}
          style={{ height: '400px' }}
          theme="ug-dashboard"
        />
      </div>

      {/* Educational Access Pyramid */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Educational Access Pyramid
          </h3>
          <p className="text-sm text-gray-600">
            Visualize the educational pathway structure showing progression from
            Primary to Secondary to Tertiary levels
          </p>
        </div>
        <ReactECharts
          option={pyramidOption}
          style={{ height: '500px' }}
          theme="ug-dashboard"
        />
      </div>

      {/* Private Sector Penetration */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Private Sector Penetration by Education Level
          </h3>
          <p className="text-sm text-gray-600">
            Track private sector penetration rates across different education
            levels to monitor privatization trends
          </p>
        </div>
        <ReactECharts
          option={privatePenetrationOption}
          style={{ height: '400px' }}
          theme="ug-dashboard"
        />
      </div>

      {/* Geographic Education Equity Heatmap */}
      {geographicEquityHeatmapData &&
        geographicEquityHeatmapData.locations.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              🗺️ Geographic Education Equity by{' '}
              {locationLevel === 'district'
                ? 'District'
                : locationLevel === 'subcounty'
                  ? 'Subcounty'
                  : locationLevel === 'parish'
                    ? 'Parish'
                    : 'Village'}
            </h3>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Identify areas with adequate versus insufficient educational
                infrastructure to guide resource allocation
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="text-left text-sm font-medium text-gray-700 p-2">
                      Location
                    </th>
                    {geographicEquityHeatmapData.years.map(year => (
                      <th
                        key={year}
                        className="text-center text-sm font-medium text-gray-700 p-2 min-w-20"
                      >
                        {year}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {geographicEquityHeatmapData.locations.map(location => (
                    <tr key={location} className="border-t">
                      <td className="text-sm font-medium text-gray-900 p-2">
                        {location}
                      </td>
                      {geographicEquityHeatmapData.years.map(year => {
                        const cellData = geographicEquityHeatmapData.data.find(
                          d => d.location === location && d.year === year
                        )
                        return (
                          <td key={year} className="text-center p-2">
                            <div
                              className="w-16 h-8 rounded flex items-center justify-center text-white text-xs font-medium mx-auto"
                              style={{
                                backgroundColor: cellData?.color || '#6b7280',
                              }}
                              title={`${location}, ${year}: ${cellData?.total || 0} institutions`}
                            >
                              {cellData?.total || 0}
                            </div>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 text-sm text-gray-600">
              <p className="font-medium">
                Total institutions across locations and years
              </p>
              <p className="flex items-center space-x-4 mt-2">
                <span className="flex items-center">
                  <span className="inline-block w-3 h-3 bg-green-500 rounded mr-1"></span>
                  High Density (≥30)
                </span>
                <span className="flex items-center">
                  <span className="inline-block w-3 h-3 bg-yellow-500 rounded mr-1"></span>
                  Moderate Density (15-29)
                </span>
                <span className="flex items-center">
                  <span className="inline-block w-3 h-3 bg-red-500 rounded mr-1"></span>
                  Low Density (&lt;15)
                </span>
              </p>
            </div>
          </div>
        )}

      {/* Data Insights */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h4 className="font-semibold text-gray-900 mb-3">📋 Key Insights</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
          <div>
            <strong>Infrastructure Growth:</strong> Track how educational
            infrastructure is expanding across different levels.
          </div>
          <div>
            <strong>Public vs Private:</strong> Monitor the balance between
            government and private sector education provision.
          </div>
          <div>
            <strong>Access Pyramid:</strong> Visualize the educational pathway
            structure from primary to tertiary level.
          </div>
          <div>
            <strong>Geographic Equity:</strong> Identify areas with adequate vs
            insufficient educational infrastructure.
          </div>
          <div>
            <strong>Privatization Trends:</strong> Track private sector
            penetration across different education levels.
          </div>
          <div>
            <strong>Capacity Planning:</strong> Use data to inform resource
            allocation and infrastructure development priorities.
          </div>
        </div>
      </div>
    </div>
  )
}

InstitutionCountTrends.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      district: PropTypes.string,
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      level: PropTypes.oneOf(['Primary', 'Secondary', 'Tertiary', '']),
      ownership: PropTypes.oneOf(['Government', 'Private', '']),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      location_code: PropTypes.string,
      thematic_area: PropTypes.string,
    })
  ),
}

export default InstitutionCountTrends
